import _ from 'lodash'
import { RestApiClient } from 'ifield-core-components'
import ImageUtil from '@/js/util/ImageUtil.js'
import { CLUSTER_ICON_ID } from '@/constants.js'
import TexturePacker from '@/lib/TexturePacker.js'
import IconCluster from '@/assets/ifield/cluster-user@3x.png'
import IconClusterSelected from '@/assets/ifield/cluster-user-selected@3x.png'
import IconTagCluster from '@/assets/ifield/cluster-tag-user@3x.png'
import IconTagClusterSelected from '@/assets/ifield/cluster-tag-user-selected@3x.png'
import IconMixedCluster from '@/assets/ifield/cluster-mixed-user@3x.png'
import IconMixedClusterSelected from '@/assets/ifield/cluster-mixed-user-selected@3x.png'
import IconAppUserPin from '@/assets/ifield/user-pin@3x.png'
import IconTagUserPin from '@/assets/ifield/tag-user-pin@3x.png'

// mapping
const MAPPING = {
  default: {
    x: 0,
    y: 0,
    width: 168,
    height: 192,
    anchorX: 84,
    anchorY: 168,
  },
  tag: {
    x: 0,
    y: 0,
    width: 168,
    height: 192,
    anchorX: 84,
    anchorY: 168,
  },
  [CLUSTER_ICON_ID.APP_DEFAULT]: {
    x: 0,
    y: 0,
    width: 288,
    height: 192,
    anchorX: 144,
    anchorY: 96,
  },
  [CLUSTER_ICON_ID.APP_SELECTED]: {
    x: 0,
    y: 0,
    width: 288,
    height: 192,
    anchorX: 144,
    anchorY: 96,
  },
  [CLUSTER_ICON_ID.TAG_DEFAULT]: {
    x: 0,
    y: 0,
    width: 288,
    height: 192,
    anchorX: 144,
    anchorY: 96,
  },
  [CLUSTER_ICON_ID.TAG_SELECTED]: {
    x: 0,
    y: 0,
    width: 288,
    height: 192,
    anchorX: 144,
    anchorY: 96,
  },
  [CLUSTER_ICON_ID.MIXED_DEFAULT]: {
    x: 0,
    y: 0,
    width: 288,
    height: 192,
    anchorX: 144,
    anchorY: 96,
  },
  [CLUSTER_ICON_ID.MIXED_SELECTED]: {
    x: 0,
    y: 0,
    width: 288,
    height: 192,
    anchorX: 144,
    anchorY: 96,
  },
  pin: {
    x: 0,
    y: 0,
    width: 168,
    height: 192,
    anchorX: 84,
    anchorY: 168,
  },
}

/**
 * URLのイメージを取得し、Image URL 形式に変換
 * @param {String} url 画像URL
 * @return {Object} promise オブジェクト
 */
const fetchImage = (url) => {
  return new Promise((resolve, reject) => {
    RestApiClient.executeGetApiInResponse(url)
      .then((response) => response.blob())
      .then((blob) => {
        // Blob で取得して、Image URL 形式に変換する
        const reader = new FileReader()
        reader.onload = function () {
          resolve(this.result)
        }
        reader.readAsDataURL(blob)
      })
      .catch((error) => {
        reject(error)
      })
  })
}
/**
 * ユーザアイコン埋め込みピン画像を取得
 * @param {String} id ユーザID
 * @return {String} dataUrl
 */
const getPinImage = async (id) => {
  const endPoint = `user/${id}/pin_image_168x192.png`
  const url = RestApiClient.createApiUrl(endPoint)
  return await fetchImage(url)
}

const fetchImageMap = new Map()

/**
 * imageStoreとiconMappingが含むIDに差分が無い場合はtrue
 * (gettersだとMapの変更が検知されないので関数)
 * @param {Map} imageStore 画像ストア
 * @param {Object} mapping iconMapping
 * @returns {Boolean}
 */
const isImageIdsSame = (imageStore, mapping) => {
  const storeKeys = [...imageStore.keys()].map((id) => `${id}`)
  const mappingKeys = mapping ? [...Object.keys(mapping)] : []
  return _.difference(storeKeys, mappingKeys).length === 0
}

/**
 * ユーザーマーカー保持Vuexモジュール
 */
export default {
  namespaced: true,
  state: {
    imageStore: new Map(), // ユーザピン画像のstore
    iconAtlas: null,
    iconMapping: null,
  },
  mutations: {
    /**
     * stateを初期化する
     * @param {*} state
     */
    resetState(state) {
      state.imageStore.clear()
      state.iconAtlas = null
      state.iconMapping = null
    },
    /**
     * 画像をストア
     * @param {*} state
     * @param {String} id ユーザID
     * @param {String} image 画像データ
     */
    setImage(state, { id, image }) {
      if (image) {
        state.imageStore.set(id, image)
      }
    },
    /**
     * iconAtlas更新
     * @param {*} state
     * @param {ImageBitmap} atlas
     */
    setAtlas(state, atlas) {
      state.iconAtlas = atlas
    },
    /**
     * iconMapping更新
     * @param {*} state
     * @param {Object} mapping
     */
    setMapping(state, mapping) {
      state.iconMapping = mapping
    },
  },
  actions: {
    /**
     * stateをリセットする
     */
    resetState({ commit }) {
      commit('resetState')
    },
    /**
     * state初期化
     * @param {Boolean} force 強制的に初期化する場合はtrue
     * @returns {Promise.<Object>} 初期化後の{atlas, mapping}
     */
    async initializeState({ state, commit, dispatch }, force) {
      if (!force && state.iconAtlas && state.iconMapping) {
        // 既に存在しているのでそのまま返却
        return { atlas: state.iconAtlas, mapping: state.iconMapping }
      }

      // imageStoreにデフォルト使用マーカー登録
      const [
        appUserPin,
        tagUserPin,
        cluster,
        clusterSelected,
        tagCluster,
        tagClusterSelected,
        mixedCluster,
        mixedClusterSelected,
      ] = await Promise.all([
        ImageUtil.toImage(IconAppUserPin),
        ImageUtil.toImage(IconTagUserPin),
        ImageUtil.toImage(IconCluster),
        ImageUtil.toImage(IconClusterSelected),
        ImageUtil.toImage(IconTagCluster),
        ImageUtil.toImage(IconTagClusterSelected),
        ImageUtil.toImage(IconMixedCluster),
        ImageUtil.toImage(IconMixedClusterSelected),
      ])
      commit('setImage', {
        id: 'default',
        image: ImageUtil.toDataURL(appUserPin),
      })
      commit('setImage', { id: 'tag', image: ImageUtil.toDataURL(tagUserPin) })
      commit('setImage', {
        id: CLUSTER_ICON_ID.APP_DEFAULT,
        image: ImageUtil.toDataURL(cluster),
      })
      commit('setImage', {
        id: CLUSTER_ICON_ID.APP_SELECTED,
        image: ImageUtil.toDataURL(clusterSelected),
      })
      commit('setImage', {
        id: CLUSTER_ICON_ID.TAG_DEFAULT,
        image: ImageUtil.toDataURL(tagCluster),
      })
      commit('setImage', {
        id: CLUSTER_ICON_ID.TAG_SELECTED,
        image: ImageUtil.toDataURL(tagClusterSelected),
      })
      commit('setImage', {
        id: CLUSTER_ICON_ID.MIXED_DEFAULT,
        image: ImageUtil.toDataURL(mixedCluster),
      })
      commit('setImage', {
        id: CLUSTER_ICON_ID.MIXED_SELECTED,
        image: ImageUtil.toDataURL(mixedClusterSelected),
      })

      // texture作成
      return await dispatch('updateTexture')
    },
    /**
     * texture state 更新
     * @param {Boolean} force 強制的に初期化する場合はtrue
     * @returns {Promise.<Object>} 更新後の{atlas, mapping}
     */
    async updateTexture({ commit, state }, force) {
      if (!force && isImageIdsSame(state.imageStore, state.iconMapping)) {
        return { atlas: state.iconAtlas, mapping: state.iconMapping }
      }

      const images = await Promise.all(
        [...state.imageStore.entries()].map(async ([id, image]) => ({
          id,
          atlas: await ImageUtil.toImage(image),
          mapping: MAPPING[id] || MAPPING.pin,
        }))
      )

      const packer = new TexturePacker()
      const { atlas, mapping } = await packer.pack(images)
      commit('setAtlas', atlas)
      commit('setMapping', mapping)

      return { atlas, mapping }
    },
    /**
     * 指定IDのピン画像取得
     * @param {Number} id ユーザーID
     * @param {Boolean} [force=false] 強制的に更新する場合はtrue
     */
    async getImageById({ state, commit }, { id, force = false }) {
      if (!force && state.imageStore.has(id)) {
        return state.imageStore.get(id)
      }
      let fetchPromise
      if (fetchImageMap.has(id)) {
        fetchPromise = fetchImageMap.get(id)
      } else {
        fetchPromise = getPinImage(id)
        fetchImageMap.set(id, fetchPromise)
      }
      const image = await fetchPromise
      commit('setImage', { id, image })
      fetchImageMap.delete(id)
      return image
    },
  },
}
